/**
 * Pure view helpers for the Signups tab — column definitions, the server sort
 * field vocabulary, pagination-window math, and segment counting. Kept out of
 * the component so they are unit-testable under the `node` vitest environment.
 */
import type { SignupSegment } from "@/services/adminApi";

// Server sort field vocabulary. These strings are the cross-repo contract with
// the backend slice engine (U2/U3): the 9 org columns plus the orphan columns.
// Sending anything outside this set yields a 400 from the endpoint.
export type OrgSortField =
  | "org"
  | "owner"
  | "email"
  | "signupDate"
  | "lastActivity"
  | "daysIdle"
  | "tokensUsed"
  | "coreChats"
  | "studioProjects";

export type OrphanSortField = "account" | "name" | "email" | "signupDate";

export type SortField = OrgSortField | OrphanSortField;
export type SortDir = "asc" | "desc";

export interface ColumnDef<F extends string> {
  label: string;
  field: F;
  align?: "right";
}

/** The 9 org-table columns, in outcomes order. NO Rubric/Practice columns. */
export const ORG_COLUMNS: ColumnDef<OrgSortField>[] = [
  { label: "Org", field: "org" },
  { label: "Owner", field: "owner" },
  { label: "Email", field: "email" },
  { label: "Signup date", field: "signupDate" },
  { label: "Last activity", field: "lastActivity" },
  { label: "Days idle", field: "daysIdle", align: "right" },
  { label: "Tokens used", field: "tokensUsed", align: "right" },
  { label: "Core chats", field: "coreChats", align: "right" },
  { label: "Studio projects", field: "studioProjects", align: "right" },
];

/** The narrower orphan-table columns. */
export const ORPHAN_COLUMNS: ColumnDef<OrphanSortField>[] = [
  { label: "Account", field: "account" },
  { label: "Name", field: "name" },
  { label: "Email", field: "email" },
  { label: "Signup date", field: "signupDate" },
];

/** Default sort on load and on segment switch. */
export const DEFAULT_SORT: { field: SortField; dir: SortDir } = {
  field: "signupDate",
  dir: "desc",
};

/** True when `field` is a valid sort column for `segment` (guards the 400). */
export function isSortFieldValidForSegment(
  field: SortField,
  segment: SignupSegment
): boolean {
  const cols = segment === "orphan" ? ORPHAN_COLUMNS : ORG_COLUMNS;
  return cols.some((c) => c.field === field);
}

export interface PageWindow {
  /** 1-based index of the first visible row (0 when empty). */
  rangeStart: number;
  /** 1-based index of the last visible row. */
  rangeEnd: number;
  isFirstPage: boolean;
  /** True when there are no further rows past this page. */
  isLastPage: boolean;
}

/** Compute the "Showing x–y of N" window and Prev/Next disabled states. */
export function pageWindow(
  skip: number,
  returnedCount: number,
  totalCount: number
): PageWindow {
  const rangeStart = totalCount === 0 ? 0 : skip + 1;
  const rangeEnd = skip + returnedCount;
  return {
    rangeStart,
    rangeEnd,
    isFirstPage: skip === 0,
    isLastPage: skip + returnedCount >= totalCount,
  };
}

/** Count projection rows per segment — the whole-dataset tab-label totals. */
export function countSegments(
  rows: { segment: SignupSegment }[]
): Record<SignupSegment, number> {
  const counts: Record<SignupSegment, number> = { real: 0, demo: 0, orphan: 0 };
  for (const r of rows) counts[r.segment]++;
  return counts;
}

/**
 * A response applies to state only if the sequence number it was issued with is
 * still the latest issued (PAR-7354 review finding [2]). Every page-fetch
 * writer — the scoping-key effect, the preset re-resolve, and Refresh — shares
 * one monotonic counter so a slow response can never clobber a newer view.
 */
export function isLatestResponse(mySeq: number, latestSeq: number): boolean {
  return mySeq === latestSeq;
}

/**
 * Adopt a fetched projection when its snapshot anchor is at least as new as the
 * currently-adopted projection anchor (PAR-7354 finding [4], relaxed round-3).
 *
 * Round-2 pinned the page anchor to the max-ever value seen (isNewerAnchor). If
 * projection adoption required strict equality with THAT page anchor, then when
 * the pinned anchor's replica stopped serving (multi-replica skew, per-process
 * caches) the 3-attempt sync could never match and the KPI/chart would freeze.
 * Making projection adoption monotonic against its OWN anchor lets the summary
 * and chart always converge forward to the newest projection any replica can
 * serve, so the sync can't be permanently futile. `>=` (not `>`) so re-adopting
 * the same snapshot is a harmless no-op rather than a wasted retry.
 */
export function shouldAdoptProjection(
  fetchedProjectionGeneratedAt: string,
  currentProjectionGeneratedAt: string | null
): boolean {
  if (currentProjectionGeneratedAt === null) return true;
  const fetched = new Date(fetchedProjectionGeneratedAt).getTime();
  if (!Number.isFinite(fetched)) return false;
  const current = new Date(currentProjectionGeneratedAt).getTime();
  if (!Number.isFinite(current)) return true;
  return fetched >= current;
}

/**
 * Monotonic snapshot-anchor adoption (PAR-7354 round-2 Bug B). Prod m365 runs
 * 1–3 replicas with non-sticky routing and per-process snapshot caches, so
 * consecutive responses can carry OLDER anchors. Only ever move the adopted
 * anchor strictly FORWARD in time, so "Data as of" and the table can never jump
 * backward and the preset re-resolve can't ping-pong between replicas.
 */
export function isNewerAnchor(incoming: string, current: string | null): boolean {
  if (current === null) return true;
  const i = new Date(incoming).getTime();
  if (!Number.isFinite(i)) return false; // unparseable incoming — never adopt
  const c = new Date(current).getTime();
  if (!Number.isFinite(c)) return true; // current unparseable — adopt a parseable one
  return i > c;
}

/**
 * Idempotent preset re-resolve gate (PAR-7354 round-2 Bug B). The preset
 * window (rangeStart = now − N days) must be re-resolved when a genuinely newer
 * snapshot anchor is adopted — but only ONCE per anchor. Returns true only when
 * a preset is active AND the current anchor differs from the anchor the active
 * preset window was already resolved against, so at most one extra table fetch
 * happens per newer snapshot (no refetch loop).
 */
export function shouldReResolvePreset(
  isPreset: boolean,
  currentAnchor: string | null,
  lastResolvedAnchor: string | null
): boolean {
  return isPreset && currentAnchor !== lastResolvedAnchor;
}
